import { Link } from "@inertiajs/react"
import { Coins, Gift } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatStrictDistance } from "@/lib/relative-time"
import { missionPath } from "@/routes"
import type { Locale } from "@/types"
import type {
  ShipmentRedemptionRow,
  ShipmentRow as Row,
} from "@/types/shipments"

import { ConfirmAddressDialog } from "./confirm-address-dialog"
import { ProductChoiceDialog } from "./product-choice-dialog"
import { RemindCreatorButton } from "./remind-creator-button"
import { ShipmentAddressBlock } from "./shipment-address-block"
import { ShipmentProductBlock } from "./shipment-product-block"

export type ShipmentSection = "ready" | "waiting" | "shipped"

export interface ShipmentRowProps {
  row: Row
  section: ShipmentSection
  locale: Locale
  /** Rendered in the actions cell — the mark-shipped dialog, supplied by the page. */
  action?: React.ReactNode
  /**
   * OKS-991 — what this row OWNS.
   *
   * `"standalone"` (default) — the row states its own creator and its own
   * destination. Waiting and Recently-shipped, and every pre-OKS-991 caller.
   *
   * `"parcel-item"` — the row is a line inside a parcel card that already
   * states the creator once and discloses ONE address for the whole box, so
   * this row states neither.
   *
   * ⚠️ THE ADDRESS SLOT IS SUPPRESSED BY THIS PROP, NOT BY THE ABSENT KEY.
   * Ready items legitimately carry no `shipping_address` (the parcel owns the
   * disclosure), and the falsy arm below is SECTION-aware: left to itself it
   * would render "no address on file" on every item of a parcel that has a
   * confirmed one — a false statement about the data, on the headline
   * surface, composed out of two individually correct pieces. Found by LQE in
   * review before it shipped.
   */
  variant?: "standalone" | "parcel-item"
}

/** `@handle` when present, else the creator's name. Never an empty label. */
export function creatorLabelFor(row: Row): string {
  return row.creator.handle ? `@${row.creator.handle}` : row.creator.name
}

/**
 * OKS-985 — the redemption row's identity line (UX Spec §2.2): cover
 * thumbnail, `Gift` icon, reward title, a "Reward" badge and the coin cost.
 *
 * ⚠️ The title is PLAIN TEXT, never a link. The only brand-side reward page
 * is an editing surface, and a mis-tap during a packing run must not land an
 * operator in a live reward's edit form (UX-OQ-2).
 *
 * ⚠️ The "Reward" badge is a WORD, not a colour or a bare icon — a
 * screen-reader user gets the same distinction a sighted operator does
 * (UX Spec §6).
 */
function RewardIdentity({ row }: { row: ShipmentRedemptionRow }) {
  const { t } = useTranslation()
  // Cover images are remote URLs that can 404 independently of the row being
  // valid, so failure falls back to the kind-icon tile rather than leaving a
  // broken-image glyph in a packing queue.
  const [coverFailed, setCoverFailed] = useState(false)
  const showCover = row.reward_cover_url && !coverFailed

  return (
    <div className="flex items-start gap-2">
      {showCover ? (
        <img
          src={row.reward_cover_url ?? undefined}
          alt=""
          className="size-10 shrink-0 rounded-md object-cover"
          onError={() => setCoverFailed(true)}
          data-testid="shipments-row-reward-cover"
        />
      ) : (
        <span
          className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-md"
          data-testid="shipments-row-reward-cover-fallback"
        >
          <Gift className="text-muted-foreground size-4" aria-hidden="true" />
        </span>
      )}

      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="text-sm font-medium break-words"
            data-testid="shipments-row-reward"
          >
            {row.reward_title}
          </span>
          <Badge variant="outline" data-testid="shipments-row-reward-badge">
            {t("shipments.row.reward_badge")}
          </Badge>
        </div>
        <span
          className="text-muted-foreground flex items-center gap-1 text-xs"
          data-testid="shipments-row-coin-cost"
        >
          <Coins className="size-3 text-amber-500" aria-hidden="true" />
          {t("shipments.row.coin_cost", { count: row.coin_cost })}
          {/* OKS-987 — quantity IS a fact about a redemption, and always
              stated: a packer must not have to infer that silence means one.
              A redemption is one reward by construction (`redemptions` has no
              quantity column), so this is a constant, not a read.

              ⚠️ NO variant line here. `rewards` has no variant table, so a
              "no variants" sentence on a reward row would be permanent, could
              never say anything else, and would be noise rather than
              disclosure — BRIEF §3.2. */}
          {" · "}
          {t("collabs.ship.product.qty", { count: 1 })}
        </span>
      </div>
    </div>
  )
}

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}

/**
 * OKS-984 — one shipment, as a stacked card (UX Spec §5, <md) and as the body
 * of a table row (≥md). The page decides which wrapper; this owns the content
 * so the two cannot drift in what they disclose.
 *
 * # ⚠️ Branch on PRESENCE, never truthiness
 *
 * `shipping_address` is ABSENT from the row outside the reveal window — not
 * null. `row.shipping_address ? … : …` happens to work today, but the contract
 * the server guarantees is absence, and a falsy-check invites someone to
 * "helpfully" start emitting `null`, which is the exact shape the gate refuses
 * to produce. Read as: no key ⇒ nothing to show.
 *
 * # Former crew still get their parcel
 *
 * A non-active membership is TAGGED, not hidden (PRD OQ-9). Someone who left
 * the crew after a brand promised them a product is still owed it.
 */
export function ShipmentRowContent({
  row,
  section,
  locale,
  action,
  variant = "standalone",
}: ShipmentRowProps) {
  const { t } = useTranslation()
  const label = creatorLabelFor(row)
  const address = row.shipping_address
  const ageSource = section === "shipped" ? row.shipped_at : row.to_ship_at
  // The parcel card states the creator and the wait once, in its header.
  const ownsIdentity = variant === "standalone"

  return (
    <div
      className="flex flex-col gap-3"
      data-testid={`shipments-row-${row.source}-${row.id}`}
    >
      {ownsIdentity && (
        <div className="flex flex-wrap items-center gap-2">
          <Avatar className="size-8">
            <AvatarFallback>{initialsFor(row.creator.name)}</AvatarFallback>
          </Avatar>
          <span className="font-medium break-words">{label}</span>
          {!row.creator.active && (
            <Badge variant="outline" data-testid="shipments-row-former-crew">
              {t("shipments.row.former_crew")}
            </Badge>
          )}
          {ageSource && (
            <Tooltip>
              {/* ⚠️ Base UI `render`, NOT Radix `asChild` — ui/ is a Base UI
                port and a zero-radix guard test reds any Radix import. */}
              <TooltipTrigger
                render={
                  <span
                    className="text-muted-foreground text-xs"
                    data-testid="shipments-row-age"
                  />
                }
              >
                {formatStrictDistance(ageSource, locale)}
              </TooltipTrigger>
              <TooltipContent>
                {new Date(ageSource).toLocaleString(
                  locale === "fr" ? "fr-FR" : "en-US",
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      {/* OKS-985 — the identity line is the ONLY thing that differs between
          the two row types (UX Spec §2.2). Everything above and below is
          shared, deliberately: an operator packing parcels should not have to
          learn two row layouts. */}
      {row.source === "collab" ? (
        /* OKS-987 — the PRODUCT is the identity now: it is what gets picked off
           a shelf. The mission is context, so it demotes to the last line of
           the same column — still a link, still wrapping, same testid. */
        <div className="flex flex-col gap-1">
          {/* OKS-1040 (PRD R2, UX §2.3) — an outstanding choice names NO
              product: never a photo, never a name, never product #1. The
              server sends `product: null` + `product_choice.pending` here. */}
          {row.product_choice?.pending ? (
            <p
              className="flex flex-col text-sm"
              data-testid="shipments-row-product-choice-pending"
            >
              <span className="font-medium">
                {t("shipments.product_choice.pending")}
              </span>
              <span className="text-muted-foreground text-xs">
                {t("shipments.product_choice.offer_count", {
                  count: row.product_choice.offer_count,
                })}
              </span>
            </p>
          ) : row.product ? (
            <ShipmentProductBlock
              product={row.product}
              /* OKS-995 F3 — the RECORD replaces the prediction once the parcel has
                 shipped (OKS-987 BRIEF §3.2, which shipped without it). Below,
                 `shipments-row-shipped-variant` states what actually went out;
                 a prediction beside it describes a decision that is over. Same
                 mechanism the dialog uses in `edit` mode. */
              compact={section === "shipped"}
            />
          ) : (
            /* Reachable, not hypothetical: `Shipments::Query` admits rows on
               `mission_type` and never joins `mission_products`, so a
               productless drop's collab lands here. A blank row would read as a
               UI failure; this says the gap is in the data. */
            <p
              className="text-muted-foreground text-sm"
              data-testid="shipments-row-no-product"
            >
              {t("collabs.ship.product.none_recorded")}
            </p>
          )}
          {/* OKS-1040 (OQ10) — a BRAND-made pick says so, in neutral words (never
              colour alone). Creator/legacy picks carry no line: no line means
              the creator chose. « Modifier le choix » rides with it while the
              server offers the edit. */}
          {row.product_choice_source === "brand" && (
            <p
              className="text-muted-foreground text-xs"
              data-testid="shipments-row-product-choice-source-brand"
            >
              {t("shipments.product_choice.source_brand")}
            </p>
          )}
          {row.product_choice_action === "edit" && (
            <div className="self-start max-sm:self-stretch">
              <ProductChoiceDialog
                collabId={row.id}
                action="edit"
                creatorLabel={label}
                testIdPrefix="shipments-row"
              />
            </div>
          )}
          {/* Mission name wraps, never truncates — it is identifying info. */}
          <Link
            href={missionPath(row.mission_id)}
            className="text-primary text-sm break-words underline-offset-4 hover:underline"
            data-testid="shipments-row-mission"
          >
            {row.mission_title}
          </Link>
          {/* OKS-987 — what actually went out. The RECORDED column, shown only
              once the parcel has shipped; before that the row states what the
              queue predicts instead (inside the product block above). */}
          {section === "shipped" && (
            <p
              className="text-muted-foreground text-xs"
              data-testid="shipments-row-shipped-variant"
            >
              {row.shipped_variant_label
                ? t("collabs.ship.variant.recorded", {
                    label: row.shipped_variant_label,
                  })
                : t("collabs.ship.variant.statement.not_recorded")}
            </p>
          )}
        </div>
      ) : (
        <RewardIdentity row={row} />
      )}

      {/* OKS-988 — the re-request flag: this creator already had the product
          and asked for another anyway.

          ⚠️ The reason is rendered VERBATIM and UNCLAMPED — no `truncate`, no
          `line-clamp`. It is the one fact the operator decides on (ship, or
          cancel), and a cut sentence is worse than none: it reads as complete
          while omitting the part that explains the request. If a long reason
          makes a packing row awkward, the fix is layout, never truncation
          (UX-A7).

          Plain text, never markup — it is user input. */}
      {"rerequest" in row && row.rerequest && (
        <div className="space-y-1" data-testid="shipments-row-rerequest">
          {/* ⚠️ OKS-988 F4 — badge + flag line render ONLY when a
              prior-possession date exists. On the brand-preset override shape
              the creator has NEVER had this product, so "Redemande" would be a
              false statement about them and the date placeholder would stand
              in for a fact that does not exist. The REASON still renders
              below: it is the signal explaining why the brand's own batch
              default was overridden, and it is the whole point of having
              demanded it. */}
          {row.rerequest.received_on && (
            <div className="flex flex-wrap items-center gap-2">
              {/* A WORD, never colour alone (UX §S5 / a11y). */}
              <Badge
                variant="outline"
                data-testid="shipments-row-rerequest-badge"
              >
                {t("shipments.row.rerequest.badge")}
              </Badge>
              <span
                className="text-muted-foreground text-sm break-words"
                data-testid="shipments-row-rerequest-line"
              >
                {t("shipments.row.rerequest.flag_line", {
                  // Same locale mapping the age tooltip above uses. A DATE, not
                  // a relative count: this is read during a packing run, where
                  // "12 août" is actionable and "il y a 19 jours" is arithmetic
                  // (PRD A13). The creator's own line uses the relative form,
                  // because that is how a person thinks about their own shelf.
                  date: new Date(row.rerequest.received_on).toLocaleDateString(
                    locale === "fr" ? "fr-FR" : "en-US",
                    {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    },
                  ),
                })}
              </span>
            </div>
          )}
          <p
            className="text-sm break-words"
            data-testid="shipments-row-rerequest-reason"
          >
            <span className="text-muted-foreground">
              {t("shipments.row.rerequest.reason_label")}
            </span>{" "}
            <span>«&nbsp;{row.rerequest.reason}&nbsp;»</span>
          </p>
        </div>
      )}

      {/* ⚠️ The falsy arm is SECTION-AWARE, and that is the whole point.
          `shipping_address` is absent on a shipped row because the reveal
          window has CLOSED -- not because the creator never gave us one. Those
          are opposite instructions to an operator: "nothing to do here" versus
          "chase this person".

          Rendering `shipments.address.none` on Recently-shipped told them
          there was no address on file for a parcel they had just shipped TO
          that address -- a false statement about the data, on every row in the
          section. Worse, it trains the eye to skip a string that is TRUE and
          ACTIONABLE two sections above, in Waiting on creator.

          A shipped row needs no explanation for a missing address block: the
          parcel is gone and the shortness is the signal, exactly as UX Spec
          §2.3 specifies for the history page's non-pending rows. Found by PO
          reading the code; pre-existing from OKS-984, propagated to a second
          row type by this ticket, so fixed here. */}
      {/* ⚠️ OKS-991 — a parcel item renders NEITHER the block NOR the
          fallback line. The parcel owns the destination statement; without
          this branch the falsy arm below would tell the operator there is no
          address on file for a box that has a confirmed one. Suppression is
          an explicit BRANCH, never an inference from the absent key. */}
      {variant === "parcel-item" ? null : address ? (
        <ShipmentAddressBlock
          address={address}
          creatorLabel={label}
          locale={locale}
        />
      ) : section === "shipped" ? null : (
        <p
          className="text-muted-foreground text-sm"
          data-testid="shipments-row-no-address"
        >
          {t("shipments.address.none")}
        </p>
      )}

      {section === "shipped" && (
        <p
          className="text-muted-foreground text-sm"
          data-testid="shipments-row-tracking"
        >
          {row.tracking_number
            ? t("shipments.row.tracking_number", {
                number: row.tracking_number,
              })
            : row.tracking_url
              ? t("shipments.row.tracking_link_only")
              : t("shipments.row.tracking_none")}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {action}
        {/* OKS-1040 (UX §2.3) — « Choisir pour le créateur » comes FIRST on a
            Waiting row with an outstanding choice; the address and Remind
            controls follow. Absent (not disabled) when the server offers none. */}
        {section === "waiting" &&
          row.source === "collab" &&
          row.product_choice_action === "choose" && (
            <ProductChoiceDialog
              collabId={row.id}
              action="choose"
              creatorLabel={label}
              testIdPrefix="shipments-row"
            />
          )}
        {/* OKS-1037 — « Confirmer l'adresse » on a Waiting collab, BEFORE
            « Relancer », which stays secondary (UX §2.1, UQ3). Shown only when
            the server says the brand may act; a `creator`-locked row and a
            gear row get no control at all (not a disabled one). */}
        {section === "waiting" &&
          row.source === "collab" &&
          row.brand_address_action && (
            <ConfirmAddressDialog
              targets={[
                { id: row.id, snapshotVersion: row.snapshot_version ?? "" },
              ]}
              action={row.brand_address_action}
              creatorLabel={label}
              address={row.shipping_address}
              contact={row.contact}
              locale={locale}
              triggerVariant="default"
              testIdPrefix="shipments-row"
            />
          )}
        {section === "waiting" && (
          <RemindCreatorButton
            collabId={row.id}
            reminderAvailableAt={row.reminder_available_at}
            creatorLabel={label}
            // OKS-1040 — the same button sends the product-choice nudge here.
            productChoice={
              row.source === "collab" && row.product_choice?.pending === true
            }
          />
        )}
      </div>
    </div>
  )
}
